import { useState, useRef } from 'react';
import { Camera, Upload, X, Loader2, Receipt as ReceiptIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';

const PAYMENT_METHODS = [
  { value: 'card', label: 'Company Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'director-loan', label: 'Director Loan' },
  { value: 'on-account', label: 'On Account (supplier credit)' },
];

/**
 * Receipt scanner dialog. Two paths into the same upload endpoint:
 *
 * 1. **Take photo** — uses `<input capture="environment">` so iOS/Android
 *    open the rear camera in receipt-friendly portrait mode.
 * 2. **Choose file** — desktop file picker / Photos library on mobile.
 *
 * Once the user picks an image we show a quick preview, let them set the
 * payment method + an optional hint, then upload to /api/bills/upload-receipt
 * which runs GPT-4o Vision and creates a bill in the Bills Inbox.
 */
const ScanReceiptDialog = ({ open, onOpenChange, onParsed }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setHint('');
    setPaymentMethod('card');
    setBusy(false);
  };

  const handleClose = () => {
    if (!busy) {
      reset();
      onOpenChange(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error('Image is larger than 12 MB — try a JPEG-quality photo');
      e.target.value = '';
      return;
    }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (f.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(f));
    } else {
      setPreviewUrl('');  // PDFs don't get an inline preview
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('paymentMethod', paymentMethod);
    fd.append('hint', hint);
    try {
      const { data } = await api.post('/bills/upload-receipt', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success(`Receipt parsed: ${data.supplierName || 'Unknown'} · £${(data.total || 0).toFixed(2)}`);
      reset();
      onOpenChange(false);
      if (onParsed) onParsed(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Could not parse the receipt — try a clearer photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="scan-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-wide">
            <ReceiptIcon className="h-4 w-4" />
            Scan or upload receipt
          </DialogTitle>
        </DialogHeader>

        {!file && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Hidden inputs — buttons trigger them. The `capture` attribute is
                respected by mobile Safari/Chrome and ignored on desktop. */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
              data-testid="receipt-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              className="hidden"
              data-testid="receipt-file-input"
            />
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="aspect-square rounded-md border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 transition flex flex-col items-center justify-center gap-2 text-slate-700"
              data-testid="receipt-camera-btn"
            >
              <Camera className="h-8 w-8" />
              <div className="text-sm font-medium">Take photo</div>
              <div className="text-[10px] text-slate-500 text-center px-2">Rear camera · best for in-store receipts</div>
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-md border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 transition flex flex-col items-center justify-center gap-2 text-slate-700"
              data-testid="receipt-upload-btn"
            >
              <Upload className="h-8 w-8" />
              <div className="text-sm font-medium">Upload file</div>
              <div className="text-[10px] text-slate-500 text-center px-2">JPEG · PNG · HEIC · PDF (max 12 MB)</div>
            </button>
          </div>
        )}

        {file && (
          <div className="space-y-4">
            <div className="bg-slate-100 rounded-md overflow-hidden flex items-center justify-center" style={{ minHeight: 220 }}>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Receipt preview"
                  className="max-h-80 w-auto"
                  data-testid="receipt-preview"
                />
              ) : (
                <div className="p-8 text-sm text-slate-600">PDF selected: <span className="font-mono">{file.name}</span></div>
              )}
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span className="font-mono truncate max-w-[60%]">{file.name}</span>
              <button
                type="button"
                onClick={() => { setFile(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }}
                className="text-red-700 hover:underline flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Choose different file
              </button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Paid by</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setPaymentMethod(m.value)}
                    className={`px-3 py-2 rounded-md border text-xs font-medium transition ${
                      paymentMethod === m.value
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
                    }`}
                    data-testid={`receipt-payment-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="receipt-hint" className="text-xs uppercase tracking-wide text-slate-600">Optional hint <span className="text-slate-400 normal-case">(supplier, project, anything)</span></Label>
              <Input
                id="receipt-hint"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="e.g. Toolstation — March site visit"
                disabled={busy}
                data-testid="receipt-hint-input"
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={handleClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!file || busy}
            data-testid="scan-receipt-submit-btn"
          >
            {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading receipt…</>)
                  : (<><ReceiptIcon className="h-4 w-4 mr-2" />Parse with AI</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScanReceiptDialog;
